// Callpage-ref: contexts/ThemeContext.tsx (adaptacja: storage key, type-import split)
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

/**
 * Motyw kolorystyczny (preset) — nakładka na tokeny bazowe, wybierana per user.
 * 'default' = baza (zielony, brak atrybutu). Kolejne motywy dokładamy tu + w index.css.
 */
export type ThemePreset = 'default' | 'indigo' | 'coral';

export const THEME_PRESETS: ReadonlyArray<{ value: ThemePreset; label: string }> = [
  { value: 'default', label: 'Zielony (domyślny)' },
  { value: 'indigo', label: 'Indygo' },
  { value: 'coral', label: 'Koral' },
];

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  preset: ThemePreset;
  setPreset: (preset: ThemePreset) => void;
}

const STORAGE_KEY = 'saas-v2:theme';
const PRESET_KEY = 'saas-v2:theme-preset';

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage może być niedostępny (private mode, quota) — fallback do light.
  }
  return 'light';
}

function isPreset(value: unknown): value is ThemePreset {
  return value === 'default' || value === 'indigo' || value === 'coral';
}

function getStoredPreset(): ThemePreset {
  if (typeof window === 'undefined') return 'default';
  try {
    const stored = localStorage.getItem(PRESET_KEY);
    if (isPreset(stored)) return stored;
  } catch {
    // jw.
  }
  return 'default';
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [preset, setPresetState] = useState<ThemePreset>(getStoredPreset);
  const isDark = theme === 'dark';

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (preset === 'default') root.removeAttribute('data-theme-preset');
    else root.setAttribute('data-theme-preset', preset);
  }, [preset]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'dark' || e.newValue === 'light')) {
        setThemeState(e.newValue);
      }
      if (e.key === PRESET_KEY && isPreset(e.newValue)) {
        setPresetState(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = (next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // patrz wyżej.
    }
  };

  const setPreset = (next: ThemePreset) => {
    setPresetState(next);
    try {
      localStorage.setItem(PRESET_KEY, next);
    } catch {
      // jw.
    }
  };

  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ theme, isDark, setTheme, toggleTheme, preset, setPreset }}>
      {children}
    </ThemeContext.Provider>
  );
}

/** Wariant bez wymogu providera — dla komponentów renderowanych też poza powłoką (np. w testach). */
export function useIsDarkTheme(): boolean {
  return useContext(ThemeContext)?.isDark ?? false;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
